"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import Icon from "@/components/ui/Icon/Icon";

const DURATION = 4000;
const EXIT_MS = 380;

type ToastType = "success" | "error" | "warning" | "info";

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  closing?: boolean;
}

interface ToastValue {
  notify: (type: ToastType, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastValue>({
  notify: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
});

export const useToast = () => useContext(ToastContext);

const TYPE_ICON: Record<ToastType, string> = {
  success: "check-circle",
  error: "x-circle",
  warning: "alert-triangle",
  info: "info",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [mounted, setMounted] = useState(false);
  const idRef = useRef(0);

  useEffect(() => setMounted(true), []);

  const remove = useCallback((id: number) => {
    setToasts((list) =>
      list.map((t) => (t.id === id ? { ...t, closing: true } : t))
    );
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, EXIT_MS);
  }, []);

  const notify = useCallback(
    (type: ToastType, message: string) => {
      const id = ++idRef.current;
      setToasts((list) => [{ id, type, message }, ...list]);
      window.setTimeout(() => remove(id), DURATION);
    },
    [remove]
  );

  const value = useMemo<ToastValue>(
    () => ({
      notify,
      success: (m) => notify("success", m),
      error: (m) => notify("error", m),
      warning: (m) => notify("warning", m),
      info: (m) => notify("info", m),
    }),
    [notify]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {mounted &&
        createPortal(
          <div className="toast-stack">
            {toasts.map((t) => (
              <div
                key={t.id}
                className={`toast is-${t.type}${t.closing ? " is-closing" : ""}`}
                role="alert"
              >
                <Icon
                  className="toast--icon"
                  name={TYPE_ICON[t.type]}
                  size={24}
                />
                <span className="toast--message">{t.message}</span>
                <button
                  className="toast--close"
                  type="button"
                  onClick={() => remove(t.id)}
                  aria-label="Закрыть"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}
